"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function () {
  const steps = [{
    number: "01",
    title: "PRE-PLANNING",
    description: "Choosing a target that's actually reachable from a city backyard. Stellarium on the desktop narrows the candidates, then Stellarium mobile in the yard—using AR to trace where the target will sit, and when—around the trees, powerlines, and light domes that frame the usable sky.",
    duration: "DAYS AHEAD"
  }, {
    number: "02",
    title: "SETUP",
    description: "Polar alignment on the AM5, focusing with the EAF, and guide calibration. A harmonic mount forgives a lot, but the night goes better when this hour goes right.",
    duration: "DUSK"
  }, {
    number: "03",
    title: "TEST EXPOSURES",
    description: "Short subs to verify focus, check backfocus across the frame, and dial in exposure times before committing the whole night. Star shapes in the corners tell the truth.",
    duration: "TWILIGHT"
  }, {
    number: "04",
    title: "IMAGING",
    highlight: true,
    description: "Once everything checks out, the ASIAIR takes over on autorun—usually all night. Then it's hoping clouds (or animals) don't end the session early, and waking up every few hours to check status. The sleep is bad. The data is worth it.",
    duration: "ALL NIGHT"
  }, {
    number: "05",
    title: "MORNING FLATS",
    description: "Calibration frames captured before teardown to remove dust motes and vignetting from the optical train. With the ASI2600MC Pro, bias and flats are all it takes—no darks required.",
    duration: "SUNRISE"
  }, {
    number: "06",
    title: "EDITING",
    description: "PixInsight only—the dedicated astrophotography suite trusted across the community. Calibration, integration, and processing turn a night of raw subs into the finished observation.",
    duration: "CLOUDY DAYS"
  }];
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section data-section-id="3595" id="solar-methodology" className="bg-background text-foreground py-24 md:py-32 px-6 md:px-12 lg:px-20">
      <div className="max-w-7xl mx-auto">
        {}
        <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 mb-24 md:mb-32 transition-all duration-1000 ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="lg:col-span-7">
            <p className="font-mono text-xs md:text-sm text-foreground mb-8 tracking-widest">
              [ 03 / METHOD ]
            </p>
            <h2 className="font-default font-bold text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tighter uppercase text-foreground">
              The Observation
              <br />
              <span className="text-foreground/40">/ Pipeline</span>
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pt-6 flex items-start">
            <p className="font-serif text-xl md:text-2xl leading-relaxed text-foreground/80">
              Six phases from backyard planning to final image—worked around trees, powerlines, city light, and the occasional lost night of sleep.
            </p>
          </div>
        </div>

        {}
        <div className="relative">
          {}
          <div className="absolute top-3 bottom-3 w-px bg-border left-6 md:left-[33.333%]" aria-hidden="true" />

          <div className="space-y-24 md:space-y-32">
            {steps.map((step, i) => {
            return <TimelineStep key={step.number} step={step} i={i} />;
          })}
          </div>
        </div>

        {}
        <div className="mt-32 md:mt-40 text-center">
          <p className="font-serif italic text-xl md:text-2xl text-foreground/80 mb-10 max-w-2xl mx-auto">
            Every observation is a singular record of a moment in cosmic time.
          </p>
          <Link to="/ObservatoryPage" className="inline-block">
            <Button variant="outline" className="font-mono text-xs tracking-widest uppercase border-foreground text-foreground bg-transparent hover:bg-foreground hover:text-background h-14 px-10 rounded-md shadow-md transition-all duration-300">
              Explore the Logbook
              <ArrowRight className="ml-2 size-4" />
            </Button>
          </Link>
        </div>
      </div>
    </section>;
}
function TimelineStep({
  step,
  i
}: {
  step: {
    number: string;
    title: string;
    description: string;
    duration: string;
    highlight?: boolean;
  };
  i: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <div ref={ref} data-index={i} className={`relative grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-12 transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} style={{
    transitionDelay: `${i * 80}ms`
  }}>
      {}
      <div className="md:col-span-4 relative flex items-start gap-6 md:block">
        {}
        <div className="absolute md:absolute left-0 md:left-auto md:right-[-13px] top-2 size-6 flex items-center justify-center z-10" aria-hidden="true">
          <div className={`size-6 border border-foreground ${step.highlight ? "bg-destructive border-destructive shadow-lg" : "bg-background"}`} />
        </div>

        {}
        <p className="font-mono text-sm md:text-base text-foreground tracking-widest pl-12 md:pl-0 md:pr-12 md:text-right">
          [ {step.number} ]
        </p>
      </div>

      {}
      <div className="md:col-span-8 pl-12 md:pl-8">
        <h3 className="font-default font-bold text-3xl md:text-5xl tracking-tighter uppercase text-foreground mb-5 leading-none">
          {step.title}
        </h3>
        <p className="font-serif text-lg md:text-xl leading-relaxed text-foreground/80 mb-6 max-w-2xl">
          {step.description}
        </p>
        <div className="flex items-center gap-4 mb-6">
          <span className="font-mono text-xs tracking-widest text-foreground/60 border border-border px-3 py-1.5">
            [ {step.duration} ]
          </span>
          {step.highlight && <span className="font-mono text-xs tracking-widest text-primary-foreground bg-destructive px-3 py-1.5">
              [ WEATHER PERMITTING ]
            </span>}
        </div>
        <div className="h-px bg-border w-full max-w-2xl" />
      </div>
    </div>;
}